import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  keyframes,
  Avatar,
  InputAdornment,
  useTheme,
  Grid,
  Card,
  CardActionArea
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MapIcon from '@mui/icons-material/Map';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChatStream } from '../../hooks/useChatStream';
import { MDBox } from '../../components/MDBox';
import { MDTypography } from '../../components/MDTypography';
import { MDInput } from '../../components/MDInput';
import { ChatHistory } from './ChatHistory';
import { AISession } from '../../services/aiService';
import { useAuthContext } from '../../contexts/authContext';

// --- Constants & Animations ---
const CHAT_PRIMARY_COLOR = '#f77973';
const CHAT_BG_COLOR = '#F8F9FA'; // Light clean background for the chat area

const messageAnimation = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const typingDot = keyframes`
  0% { transform: translateY(0px); opacity: 0.5; }
  50% { transform: translateY(-5px); opacity: 1; }
  100% { transform: translateY(0px); opacity: 0.5; }
`;

export const FairFareAI = () => {
  const { 
    messages, 
    sendMessage, 
    isLoading, 
    sessionId, 
    loadSession, 
    startNewSession 
  } = useChatStream();
  
  const { user } = useAuthContext();
  const userName = user?.fullName?.split(' ')[0] || 'there';

  const [input, setInput] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLength = useRef(messages.length);
  const prevSessionIdRef = useRef<string | null>(null);

  const theme = useTheme();

  // Topic Tile Handler
  const handleTileClick = (prompt: string) => {
    sendMessage(prompt);
  };

  // Detect New Session Creation to refresh Sidebar
  useEffect(() => {
    if (prevSessionIdRef.current === null && sessionId !== null) {
      setRefreshTrigger(prev => prev + 1);
    }
    prevSessionIdRef.current = sessionId;
  }, [sessionId]);

  // Handle Session Selection from Sidebar
  const handleSelectSession = (session: AISession) => {
    const mappedMessages = (session.messages || []).map(m => ({
      role: m.role as 'user' | 'assistant' | 'system',
      content: m.content
    }));
    loadSession(session.id, mappedMessages);
  };

  const handleNewChat = () => {
    startNewSession();
  };

  // Smart auto-scroll
  useEffect(() => {
    if (!bottomRef.current || !scrollContainerRef.current) return;

    const isNewMessage = messages.length > prevMessagesLength.current;
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;

    if (isNewMessage) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (isNearBottom) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }

    prevMessagesLength.current = messages.length;
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
  };

  return (
    <MDBox
      sx={{
        height: 'calc(100vh - 120px)', // Adjust based on navbar height
        display: 'flex',
        flexDirection: 'row',
        bgcolor: '#ffffff',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: theme.shadows[3]
      }}
    >
      {/* --- Left: Main Chat Area --- */}
      <MDBox 
        flexGrow={1} 
        display="flex" 
        flexDirection="column"
        height="100%"
        position="relative"
        bgcolor={CHAT_BG_COLOR}
      >
        {/* Messages Scroll Area */}
        <div 
          ref={scrollContainerRef} 
          style={{ 
            flexGrow: 1, 
            overflowY: 'auto', 
            paddingBottom: '100px', // Space for the floating input
            paddingTop: '20px'
          }}
        >
          <MDBox 
            sx={{ 
              maxWidth: '900px', 
              margin: '0 auto', 
              px: { xs: 2, md: 3 } 
            }}
          >
            {messages.length === 0 && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                mt={4}
                mb={4}
              >
                {/* Greeting */}
                <Box textAlign="center" mb={6}>
                  <MDTypography variant="h3" fontWeight="bold" gutterBottom>
                    Hi <span style={{ color: CHAT_PRIMARY_COLOR }}>{userName}</span>
                  </MDTypography>
                  <MDTypography variant="h4" color="text" sx={{ opacity: 0.7 }}>
                    How can I help you today?
                  </MDTypography>
                </Box>

                {/* Topics Grid */}
                <Grid container spacing={3} justifyContent="center" sx={{ mb: 6, maxWidth: '800px' }}>
                  {[
                    {
                      label: 'Claims & Bills',
                      icon: <ReceiptLongIcon fontSize="large" />,
                      prompt: 'Show me my recent claims and bills breakdown.'
                    },
                    {
                      label: 'Eligibility',
                      icon: <VerifiedUserIcon fontSize="large" />,
                      prompt: 'Give a breakdown of members policy product by product code'
                    },
                    {
                      label: 'Steerage',
                      icon: <MapIcon fontSize="large" />,
                      prompt: 'Give me a steerages breakdown for my members by states.'
                    }
                  ].map((topic) => (
                    <Grid item xs={12} sm={4} key={topic.label}>
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: '16px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                          },
                          border: '1px solid rgba(0,0,0,0.05)'
                        }}
                      >
                        <CardActionArea 
                          onClick={() => handleTileClick(topic.prompt)}
                          sx={{ 
                            p: 3, 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            textAlign: 'center' 
                          }}
                        >
                          <Box 
                            sx={{ 
                              color: CHAT_PRIMARY_COLOR, 
                              mb: 2,
                              p: 1.5,
                              borderRadius: '50%',
                              bgcolor: 'rgba(247, 121, 115, 0.1)' 
                            }}
                          >
                            {topic.icon}
                          </Box>
                          <MDTypography variant="h6" fontWeight="medium">
                            {topic.label}
                          </MDTypography>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Example Prompts with Caption */}
                <Box maxWidth="600px" width="100%" textAlign="center">
                  <MDTypography 
                    variant="caption" 
                    textTransform="uppercase" 
                    fontWeight="bold" 
                    color="text" 
                    sx={{ opacity: 0.5, display: 'block', mb: 2 }}
                  >
                    Try asking about...
                  </MDTypography>
                  <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
                    {[
                      "How many claims were processed last month?",
                      "Are there any denied bills?",
                      "Show me recent tasks"
                    ].map((text, i) => (
                      <Box
                        key={i}
                        onClick={() => setInput(text)}
                        sx={{
                          bgcolor: '#fff',
                          border: '1px solid rgba(0,0,0,0.1)',
                          borderRadius: '20px',
                          px: 2,
                          py: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'rgba(247, 121, 115, 0.05)',
                            borderColor: CHAT_PRIMARY_COLOR,
                            color: CHAT_PRIMARY_COLOR
                          }
                        }}
                      >
                        <MDTypography variant="button" textTransform="none" fontSize="0.85rem">
                         &quot;{text}&quot;
                        </MDTypography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            {messages.map((msg, idx) => {
              const isUser = msg.role === 'user';
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    gap: 2,
                    mb: 4,
                    animation: `${messageAnimation} 0.3s ease-out`,
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      bgcolor: isUser ? CHAT_PRIMARY_COLOR : '#fff',
                      color: isUser ? '#fff' : CHAT_PRIMARY_COLOR,
                      boxShadow: theme.shadows[1],
                      width: 40,
                      height: 40
                    }}
                  >
                    {isUser ? <PersonIcon /> : <SmartToyIcon />}
                  </Avatar>

                  {/* Message Bubble */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      maxWidth: '80%',
                      borderRadius: isUser ? '20px 0 20px 20px' : '0 20px 20px 20px',
                      bgcolor: isUser ? CHAT_PRIMARY_COLOR : '#fff',
                      color: isUser ? '#fff' : theme.palette.text.primary,
                      boxShadow: isUser ? theme.shadows[4] : theme.shadows[1],
                      position: 'relative'
                    }}
                  >
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Override basic elements to fit the theme
                        p: ({node, ...props}) => <p style={{ margin: 0, lineHeight: 1.6 }} {...props} />,
                        a: ({node, ...props}) => <a style={{ color: isUser ? '#fff' : CHAT_PRIMARY_COLOR, textDecoration: 'underline' }} {...props} />,
                        code: ({node, inline, ...props}: any) => (
                          inline 
                            ? <code style={{ text: 'small', backgroundColor: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: 4 }} {...props} />
                            : <div style={{ overflowX: 'auto', backgroundColor: 'rgba(0,0,0,0.05)', padding: '10px', borderRadius: '8px', marginTop: '10px' }}><code {...props} /></div>
                        )
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </Paper>
                </Box>
              );
            })}

            {/* AI Typing Indicator */}
            {isLoading && (
              <Box sx={{ display: 'flex', gap: 2, mb: 4, animation: `${messageAnimation} 0.3s ease-out` }}>
                 <Avatar
                    sx={{
                      bgcolor: '#fff',
                      color: CHAT_PRIMARY_COLOR,
                      boxShadow: theme.shadows[1],
                      width: 40,
                      height: 40
                    }}
                  >
                    <SmartToyIcon />
                  </Avatar>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '0 20px 20px 20px',
                    bgcolor: '#fff',
                    boxShadow: theme.shadows[1],
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: 'fit-content'
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: CHAT_PRIMARY_COLOR,
                        borderRadius: '50%',
                        animation: `${typingDot} 1.4s infinite ease-in-out both`,
                        animationDelay: `${i * 0.2}s`
                      }}
                    />
                  ))}
                </Paper>
              </Box>
            )}
            <div ref={bottomRef} />
          </MDBox>
        </div>

        {/* Input Area - Floating at bottom */}
        <MDBox 
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          p={3}
          sx={{
            background: `linear-gradient(to top, ${CHAT_BG_COLOR} 80%, transparent)`,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <MDBox 
            width="100%" 
            maxWidth="900px" 
            sx={{ 
              bgcolor: '#fff', 
              borderRadius: '30px',
              boxShadow: theme.shadows[4],
              p: '4px 8px 4px 20px', // Adjusted padding
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <MDInput
              fullWidth
              variant="standard" 
              placeholder="Ask anything..."
              value={input}
              disabled={isLoading}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setInput(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              InputProps={{
                disableUnderline: true, // Clean look
                style: { fontSize: '1rem', padding: '10px 0' }
              }}
              multiline
              maxRows={4}
            />
            
            <IconButton
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              sx={{
                bgcolor: input.trim() ? CHAT_PRIMARY_COLOR : 'action.disabledBackground',
                color: '#fff',
                width: 44,
                height: 44,
                ml: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: input.trim() ? '#e6605a' : 'action.disabledBackground', // Darker shade on hover
                  transform: 'scale(1.05)'
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled
                }
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* --- Right: Chat History Sidebar --- */}
      <MDBox 
        width="280px" 
        height="100%"
        sx={{
          borderLeft: `1px solid ${theme.palette.divider}`,
          display: { xs: 'none', md: 'block' }, // Hide on mobile for now
          bgcolor: '#fff'
        }}
      >
        <ChatHistory 
          currentSessionId={sessionId}
          onSelectSession={handleSelectSession}
          onNewChat={handleNewChat}
          refreshTrigger={refreshTrigger}
        />
      </MDBox>
    </MDBox>
  );
};

export default FairFareAI;